import { PropsWithChildren, useEffect, useState } from 'react';

import { useShallow } from 'zustand/react/shallow';

import { DashboardDialog, DashboardDialogProps } from '@/shared/ui';

import { MoneyFlowType } from '../../model/types';
import { useAddMoney } from '../../model/useAddMoney';
import { useSendMoney } from '../../model/useSendMoney';
import { getAddMoneyProps } from './add-money-flow/add-money';
import { addPaymentProps } from './add-payment-modal';
import { getRecipientsProps } from './contacts/contacts-modal';
import { getSendMoneyBusinessProps } from './send-money-business-flow/send-money-business';
import { getSendMoneyInnerProps } from './send-money-inner-flow/send-money-inner';
import { accountsProps } from './send-money-shared/ui/accounts';
import { addRecipientProps } from './send-money-shared/ui/add-recipient';
import { findClientProps } from './send-money-shared/ui/find-client';
import { getSendMoneySomeoneProps } from './send-money-someone-flow/send-money-someone';

interface SendMoneyFlowModalProps extends PropsWithChildren {
  defaultStep?: MoneyFlowType;
}

export const SendMoneyFlowModal = ({
  children,
  defaultStep,
}: SendMoneyFlowModalProps) => {
  const [step, setStep] = useState<MoneyFlowType | null>(null);
  const [lastStep, setLastStep] = useState<MoneyFlowType | null>('contacts');
  const [isOpen, setIsOpen] = useState(false);
  const [tab, setTab] = useState('local');

  const addMoneyStore = useAddMoney(useShallow((state) => state));
  const sendMoneyStore = useSendMoney(useShallow((state) => state));

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSetStep = (step: MoneyFlowType) => {
    setStep((prev) => {
      setLastStep(prev);
      return step;
    });
  };

  const steps: Record<MoneyFlowType, DashboardDialogProps> = {
    contacts: getRecipientsProps({ setStep: handleSetStep }),
    'add-recipient': addRecipientProps({
      setStep: handleSetStep,
      onBack: () => {
        setStep('contacts');
      },
      isFirst: defaultStep === 'add-recipient',
    }),
    'find-client': findClientProps({
      setStep: handleSetStep,
      onBack: () => {
        setStep('add-recipient');
      },
      isFirst: defaultStep === 'find-client',
    }),
    accounts: accountsProps({
      setStep: handleSetStep,
      onBack: () => {
        setStep('find-client');
      },
      onClose: () => {
        setIsOpen(false);
      },
      isFirst: defaultStep === 'accounts',
    }),
    add: getAddMoneyProps({
      onDone: handleClose,
      onBack: () => {
        setStep(lastStep);
      },
      isFirst: defaultStep === 'add',
      tab: tab,
      setTab: setTab,
      ...addMoneyStore,
    }),
    'send-business': getSendMoneyBusinessProps({
      onDone: () => {
        setIsOpen(false);
      },
      onBack: () => {
        setStep(lastStep);
      },
      isFirst: defaultStep === 'send-business',
      ...sendMoneyStore,
    }),
    'send-someone': getSendMoneySomeoneProps({
      onDone: () => {
        setIsOpen(false);
      },
      onBack: () => {
        setStep(lastStep);
      },
      isFirst: defaultStep === 'send-someone',
      ...sendMoneyStore,
    }),
    'send-inner': getSendMoneyInnerProps({
      onDone: () => {
        setIsOpen(false);
      },
      onBack: () => {
        setStep(lastStep);
      },
      isFirst: defaultStep === 'send-inner',
      ...sendMoneyStore,
    }),
    'choose-flow': addPaymentProps({
      setStep: handleSetStep,
      onBack: () => {
        setStep('contacts');
      },
      isFirst: defaultStep === 'choose-flow',
    }),
  };

  useEffect(() => {
    setStep(defaultStep ?? 'contacts');
  }, [defaultStep, isOpen]);

  return (
    step && (
      <DashboardDialog
        titleProps={{ variant: 'm' }}
        triggerProps={{
          asChild: true,
          onClick: () => {
            setIsOpen(true);
          },
        }}
        closeButtonProps={{
          onClick: () => {
            setIsOpen(false);
          },
        }}
        onClose={() => {
          setIsOpen(false);
        }}
        disableOutsideClick={sendMoneyStore.step !== 'success'}
        onOpenChange={setIsOpen}
        open={isOpen}
        {...steps[step]}
      >
        {children}
      </DashboardDialog>
    )
  );
};
